import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adminService } from '../../services';
import { POLLING_CONFIG } from '../../config/polling';

interface AdminDashboardData {
  totalOrganizations: number;
  totalUsers: number;
  totalServiceProviders: number;
  totalServices: number;
  activeAnalyses: number;         // Legacy - alias für analyzingAnalyses
  analyzingAnalyses?: number;     // 🔄 Wird analysiert (neue Kategorie)
  completedAnalyses: number;
  openAnalyses: number;
  pendingAnalyses: number;
  lastUpdated: string;
  // Debug-Felder
  statusBreakdown?: Record<string, number>;
  totalContractPackages?: number;
}

const AdminOverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Dashboard-Daten laden
  const loadDashboardData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setIsPolling(true);

      const data = await adminService.getDashboardStatus();

      // 🔧 PERFORMANCE: Nur State updaten wenn sich Daten wirklich geändert haben
      setDashboardData(prevData => {
        if (!prevData) return data;

        // Vergleiche wichtige Felder
        const hasChanges =
          prevData.totalOrganizations !== data.totalOrganizations ||
          prevData.totalUsers !== data.totalUsers ||
          prevData.totalServiceProviders !== data.totalServiceProviders ||
          prevData.totalServices !== data.totalServices ||
          prevData.activeAnalyses !== data.activeAnalyses ||
          prevData.completedAnalyses !== data.completedAnalyses ||
          prevData.openAnalyses !== data.openAnalyses ||
          prevData.pendingAnalyses !== data.pendingAnalyses;

        return hasChanges ? data : prevData;
      });

      setError(null);
    } catch (err) {
      console.error('Error loading admin dashboard data:', err);
      setError(t('admin:messages.loadError'));
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, []);

  // Initiales Laden
  useEffect(() => {
    loadDashboardData(true);
  }, []);

  // ⚡ GLOBALES POLLING SYSTEM ⚡
  useEffect(() => {
    if (!POLLING_CONFIG.ENABLE_POLLING) return;

    const pollData = async () => {
      if (!isPolling) {
        await loadDashboardData(false); // Kein Loading-Spinner beim Polling
      }
    };

    // Registriere bei globalem Polling-Manager
    const { PollingManager } = require('../../config/polling');
    const pollingManager = PollingManager.getInstance();

    pollingManager.register('admin-dashboard', pollData);

    // Legacy-Fallback (falls globales System deaktiviert)
    const legacyInterval = !POLLING_CONFIG.ENABLE_POLLING ? setInterval(pollData, POLLING_CONFIG.ADMIN_INTERVAL) : null;

    return () => {
      pollingManager.unregister('admin-dashboard');

      if (legacyInterval) {
        clearInterval(legacyInterval);
      }
    };
  }, [isPolling, loadDashboardData]);

  const handleRefresh = () => {
    loadDashboardData(true);
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        paddingTop: '6.25rem'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ paddingTop: '6.25rem', paddingX: 4 }}>
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh} data-testid="admin-dashboard-retry-button">
              {t('admin:dashboard.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboardData) {
    return null;
  }
  return (
    <Box sx={{
      paddingTop: '6.25rem',
      paddingX: { xs: 2, sm: 3, md: 4 },
      minHeight: '100vh',
      maxWidth: '100%',
      overflowX: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{
          color: 'text.primary',
          mb: 1,
          fontWeight: 400,
          fontSize: '2rem'
        }}>
          {t('admin:dashboard.title')}
        </Typography>
        <Typography variant="body1" sx={{
          color: 'text.secondary',
          fontSize: '1rem',
          lineHeight: 1.5
        }}>
          {t('admin:dashboard.description')}
        </Typography>
      </Box>

      {/* Dashboard Statistiken - CSS Grid statt MUI Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)'
        },
        gap: { xs: 2, md: 3 },
        mb: 5
      }}>
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.organizations')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '2rem'
            }}>
              {dashboardData.totalOrganizations}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#2563eb',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.users')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#2563eb',
              fontSize: '2rem'
            }}>
              {dashboardData.totalUsers}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#dc2626',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.providers')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#dc2626',
              fontSize: '2rem'
            }}>
              {dashboardData.totalServiceProviders}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#16a34a',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.services')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#16a34a',
              fontSize: '2rem'
            }}>
              {dashboardData.totalServices}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#4caf50',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.completedAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#4caf50',
              fontSize: '2rem'
            }}>
              {dashboardData.completedAnalyses}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#ff9800',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.analyzingAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#ff9800',
              fontSize: '2rem'
            }}>
              {dashboardData.analyzingAnalyses ?? dashboardData.activeAnalyses}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: '#2196f3',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.pendingAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: '#2196f3',
              fontSize: '2rem'
            }}>
              {dashboardData.pendingAnalyses}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.875rem',
              mb: 1
            }}>
              {t('admin:dashboard.stats.openAnalyses')}
            </Typography>
            <Typography variant="h3" sx={{
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '2rem'
            }}>
              {dashboardData.openAnalyses}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Admin-Funktionen - Flexbox Layout */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
        {/* Zwei Karten nebeneinander */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: { xs: 2, md: 3 }
        }}>
          <Card sx={{
            flex: 1,
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            borderRadius: '8px',
            border: '1px solid rgb(0 0 0 / 0.05)',
            transition: 'box-shadow 150ms ease',
            '&:hover': {
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
            },
            bgcolor: 'background.paper'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{
                mb: 2,
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '1.125rem'
              }}>
                {t('admin:dashboard.orgManagement.title')}
              </Typography>
              <Typography variant="body2" sx={{
                mb: 2,
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}>
                {t('admin:dashboard.orgManagement.description')}
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.orgManagement.createOrg')}
                </Typography>
                <Typography component="li" variant="body2" sx={{
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.orgManagement.addUsers')}
                </Typography>
                <Typography component="li" variant="body2" sx={{
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.orgManagement.assignRoles')}
                </Typography>
                <Typography component="li" variant="body2" sx={{
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.orgManagement.orgSettings')}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{
            flex: 1,
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            borderRadius: '8px',
            border: '1px solid rgb(0 0 0 / 0.05)',
            transition: 'box-shadow 150ms ease',
            '&:hover': {
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
            },
            bgcolor: 'background.paper'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{
                mb: 2,
                color: 'text.primary',
                fontWeight: 500,
                fontSize: '1.125rem'
              }}>
                {t('admin:dashboard.sysManagement.title')}
              </Typography>
              <Typography variant="body2" sx={{
                mb: 2,
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}>
                {t('admin:dashboard.sysManagement.description')}
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2" sx={{
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.sysManagement.monitoring')}
                </Typography>
                <Typography component="li" variant="body2" sx={{
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.sysManagement.backup')}
                </Typography>
                <Typography component="li" variant="body2" sx={{
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.sysManagement.auditLogs')}
                </Typography>
                <Typography component="li" variant="body2" sx={{
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.sysManagement.globalConfig')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Quick Access Card */}
        <Card sx={{
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          borderRadius: '8px',
          border: '1px solid rgb(0 0 0 / 0.05)',
          transition: 'box-shadow 150ms ease',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px 0 rgb(0 0 0 / 0.06)'
          },
          bgcolor: 'background.paper'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{
              mb: 3,
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '1.125rem'
            }}>
              {t('admin:dashboard.quickAccess.title')}
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              <Box sx={{
                p: 2.5,
                bgcolor: isDark ? '#0c2d48' : '#f0f9ff',
                borderRadius: '6px',
                border: isDark ? '1px solid #1e4976' : '1px solid #bae6fd',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                '&:hover': {
                  bgcolor: isDark ? '#0f3a5c' : '#e0f2fe'
                }
              }}>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: isDark ? '#7dd3fc' : '#0369a1',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.quickAccess.newOrg')}
                </Typography>
                <Typography variant="body2" sx={{
                  color: isDark ? '#38bdf8' : '#0284c7',
                  fontSize: '0.8125rem'
                }}>
                  {t('admin:dashboard.quickAccess.newOrgDesc')}
                </Typography>
              </Box>
              <Box sx={{
                p: 2.5,
                bgcolor: isDark ? '#052e16' : '#f0fdf4',
                borderRadius: '6px',
                border: isDark ? '1px solid #14532d' : '1px solid #bbf7d0',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                '&:hover': {
                  bgcolor: isDark ? '#064e3b' : '#dcfce7'
                }
              }}>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: isDark ? '#86efac' : '#15803d',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.quickAccess.manageUsers')}
                </Typography>
                <Typography variant="body2" sx={{
                  color: isDark ? '#4ade80' : '#16a34a',
                  fontSize: '0.8125rem'
                }}>
                  {t('admin:dashboard.quickAccess.manageUsersDesc')}
                </Typography>
              </Box>
              <Box sx={{
                p: 2.5,
                bgcolor: isDark ? '#451a03' : '#fffbeb',
                borderRadius: '6px',
                border: isDark ? '1px solid #78350f' : '1px solid #fed7aa',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                '&:hover': {
                  bgcolor: isDark ? '#5c2d0e' : '#fef3c7'
                }
              }}>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: isDark ? '#fcd34d' : '#d97706',
                  fontSize: '0.875rem'
                }}>
                  {t('admin:dashboard.quickAccess.systemLogs')}
                </Typography>
                <Typography variant="body2" sx={{
                  color: isDark ? '#fbbf24' : '#ea580c',
                  fontSize: '0.8125rem'
                }}>
                  {t('admin:dashboard.quickAccess.systemLogsDesc')}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminOverviewPage; 